import { NotFoundError } from '@magicdoorinc/errors';
import { createMutation, createTriggerResource } from '@magicdoorinc/ui';
import { useNavigate } from '@solidjs/router';
import { createSignal } from 'solid-js';
import { usePropertyManagerPermissions } from '~/contexts/permissions/PropertyManagerPermissionsContext';
import { createMagicDoorContext } from '~/contexts/utils';
import { PropertiesRepository } from '~/repositories/propertiesRepository';
import type { PropertiesFilter, PropertyImagePayload } from '~/repositories/propertiesRepository';
import type {
  CreatePropertyDto,
  HydratedPropertyPaginationDto,
  PropertyOverviewDto,
  UpdatePropertyDto,
  UpdateUnitMagicTagDto,
} from '~/swagger/portal/Api';

const repo = new PropertiesRepository();

export type PropertyImage = {
  status: 'new' | 'deleted' | 'normal';
  file?: File;
  fileId?: string;
};

type FilteredPropertiesResource = (() => HydratedPropertyPaginationDto | undefined) & {
  loading: boolean;
  error: any;
  latest: HydratedPropertyPaginationDto | undefined;
};

export const [PropertiesProvider, useProperties] = createMagicDoorContext('Properties', () => {
  const navigate = useNavigate();
  const { propertyAccess, addPropertyAccess } = usePropertyManagerPermissions();

  const [filteredResource, setFilter, { refetch: refetchFiltered, mutate: mutateFiltered }] = createTriggerResource(
    async (filter: PropertiesFilter) => await repo.getProperties({ active: true, ...filter })
  );

  const applyPropertyAccessFilter = (data: HydratedPropertyPaginationDto | undefined) => {
    const access = propertyAccess();

    if (!data?.items || !access) return data;
    if (access.allProperties) return data;

    const allowedPropertyIds = new Set(access.propertyIds || []);
    const allowedPortfolioIds = new Set(access.portfolioIds || []);

    const filteredItems = data.items.filter((property) => {
      if (allowedPropertyIds.has(property.id)) return true;
      if (allowedPortfolioIds.has(property.portfolioId)) return true;
      return false;
    });

    return {
      ...data,
      items: filteredItems,
      totalCount: filteredItems.length,
    };
  };

  const filteredWithAccess = () => applyPropertyAccessFilter(filteredResource());

  const filtered = Object.defineProperties(filteredWithAccess, {
    loading: { get: () => filteredResource.loading },
    error: { get: () => filteredResource.error },
    latest: { get: () => applyPropertyAccessFilter(filteredResource.latest) },
  }) as FilteredPropertiesResource;

  const [propertyOverview, setPropertyOverview] = createSignal<PropertyOverviewDto>();

  const [current, setCurrentId, { refetch: refetchProperty, mutate: mutateProperty }] = createTriggerResource(async (id?: string) => {
    if (!id) return undefined;
    try {
      return await repo.getProperty(id);
    } catch (error) {
      if (error instanceof NotFoundError) {
        navigate(`/portfolios`);
        return;
      }
      throw error;
    }
  });

  const updatePropertyMagicTags = createMutation(async (properyId: string, magicTags: UpdateUnitMagicTagDto) => {
    const updated = await repo.updatePropertyMagicTags(properyId, magicTags);
    refetchProperty(properyId);
    return updated;
  });

  const addProperty = createMutation(async (payload: CreatePropertyDto) => {
    const result = await repo.createProperty(payload);
    refetchFiltered();

    const access = propertyAccess();
    if (access && !access.allProperties) {
      const isPortfolioDirectlyAuthorized = access.portfolioIds?.includes(result.portfolioId);
      if (!isPortfolioDirectlyAuthorized) {
        await addPropertyAccess(result.id);
      }
    }

    return result;
  });

  /** Not preferred. use deactivateProperty instead in most cases */
  const deleteProperty = createMutation(async (id: string) => {
    await repo.deleteProperty(id);
    refetchProperty();
  });

  const deactivateProperty = createMutation(async (id: string, forceDeactivateChildren?: boolean) => {
    await repo.deactivateProperty(id, forceDeactivateChildren);
  });

  const updateProperty = createMutation(async (id: string, payload: UpdatePropertyDto) => {
    const result = await repo.updateProperty(id, payload);
    refetchProperty();
    return result;
  });

  const removePropertyOwner = createMutation(async (propertyId: string, ownerId: string) => {
    await repo.removePropertyOwner(propertyId, ownerId);
    refetchProperty();
  });

  const addPropertyOwner = createMutation(async (propertyId: string, ownerId: string, ownershipPercentage: number) => {
    await repo.addPropertyOwner(propertyId, ownerId, { ownershipPercentage });
    refetchProperty();
  });

  const updatePropertyOwner = createMutation(async (propertyId: string, ownerId: string, ownershipPercentage: number) => {
    await repo.updatePropertyOwner(propertyId, ownerId, { ownershipPercentage });
    refetchProperty();
  });

  const getPropertyOverview = createMutation(async (propertyId: string) => {
    const data = await repo.getPropertyOverview(propertyId, true);
    setPropertyOverview(data);
  });

  const movePropertyToPortfolio = createMutation(async (propertyId: string, portfolioId: string) => {
    await repo.moveProperty(propertyId, portfolioId);
    refetchProperty();
  });

  const uploadPropertyImage = createMutation(async (propertyId: string, payload: PropertyImagePayload) => {
    const updated = await repo.uploadPropertyImage(propertyId, payload);
    refetchProperty(propertyId);
    return updated;
  });

  return {
    get loading() {
      return () => filteredResource.loading || current.loading;
    },
    get filtered() {
      return filtered;
    },
    setFilter,
    setCurrentId,
    current,
    propertyOverview,
    getPropertyOverview,
    addProperty,
    deleteProperty,
    deactivateProperty,
    updateProperty,
    mutateProperty,
    removePropertyOwner,
    addPropertyOwner,
    updatePropertyOwner,
    updatePropertyMagicTags,
    movePropertyToPortfolio,
    uploadPropertyImage,
    refetchFiltered,
    mutateFiltered,
  };
});
